import React from "react";
import { motion } from "framer-motion";
import { PageHero, PageCta } from "@/components/PageParts";
import { usePageMeta } from "@/hooks/usePageMeta";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function AboutPage() {
  usePageMeta(
    "About — May Forward Tech",
    "May Forward Tech was founded on a simple observation: organizations pay for software built for everyone and adapted to none. We build custom tools that fit."
  );

  return (
    <>
      <PageHero
        eyebrow="About May Forward Tech"
        title="Software should work for you. Not the other way around."
        intro="That sentence is why we exist."
        image="/img/about-lines.png"
      />

      <section className="py-16 lg:py-24 bg-white border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <motion.p {...fadeUp} className="text-xl sm:text-2xl text-slate-700 leading-relaxed font-medium">
            For decades, organizations have had two bad options.
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 bg-[#FAFAFC] p-7">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3">
                Option one
              </p>
              <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-3">
                Buy the same box as everyone else
              </h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Pay for enterprise software built for a generic "customer" —
                then spend years, and millions, bending your business to fit it.
                New modules. Workarounds. Consultants who bill by the hour to
                make the tool do what you needed in the first place.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-[#FAFAFC] p-7">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3">
                Option two
              </p>
              <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-3">
                Build from scratch and hope
              </h3>
              <p className="text-base text-slate-600 leading-relaxed">
                Hire a team, wait eighteen months, and pray the result matches
                the vision. Most of the time it doesn't — and now you own a
                system nobody wants to touch.
              </p>
            </div>
          </motion.div>

          <motion.p {...fadeUp} className="text-xl sm:text-2xl text-slate-700 leading-relaxed font-medium">
            We started May Forward Tech because there is a third way now.
          </motion.p>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="text-lg text-slate-600 leading-relaxed">
            AI changed the economics of building software. What used to take a
            team of twenty and two years now takes a small senior team and a
            quarter. But AI alone isn't enough. It has to be pointed at the
            right problem, wired into your real workflows, and built by people
            who understand how organizations actually run — not how a product
            manager in Silicon Valley imagined they might.
          </motion.p>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="text-lg text-slate-600 leading-relaxed">
            That's what we do. We sit with you, learn how your work actually
            happens, and build the tool that fits — not the tool you fit into.
          </motion.p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#FAFAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-10">
            What we believe
          </motion.h2>
          <div className="space-y-6">
            {[
              {
                title: "Big Tech doesn't know your organization",
                detail:
                  "And it never will. Their incentive is to sell you the same box as everyone else, at scale. Your edge is in what makes you different — your processes, your people, your data. Software should protect that edge, not sand it down.",
              },
              {
                title: "AI is a tool, not a strategy",
                detail:
                  "Buying AI without changing how work happens is like buying a gym membership and never going. We design the workflow first, then build the AI around it — so the technology does the work, not the other way around.",
              },
              {
                title: "Custom should cost less, not more",
                detail:
                  "Off-the-shelf software charges you rent forever and fits like a rental. Custom tools, built by a senior team that ships fast, pay for themselves — and you own them.",
              },
              {
                title: "Senior people do senior work",
                detail:
                  "The person who scopes your project is the person who builds it. No handoffs, no juniors learning on your budget, no telephone game between sales and engineering.",
              },
            ].map((b, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                className="rounded-2xl border border-slate-200 bg-white p-7 lg:p-8"
              >
                <h3 className="font-display text-xl font-semibold tracking-tight text-[#0F172A] mb-3">
                  {b.title}
                </h3>
                <p className="text-base text-slate-600 leading-relaxed">{b.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-6">
            The name
          </motion.h2>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="text-lg text-slate-600 leading-relaxed mb-6">
            May Forward Tech. Because progress isn't a straight line, and the
            best path forward isn't the one someone else mapped for you. It's
            the one built around where you actually want to go.
          </motion.p>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="text-lg text-slate-600 leading-relaxed">
            Never go straight — always go forward.
          </motion.p>
        </div>
      </section>

      <PageCta />
    </>
  );
}
